import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Save, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useParams } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useWilayas, useCommunesByWilaya } from "@/hooks/useAlgeriaData";
import { useAnnonceById, useUpdateAnnonce } from "@/hooks/useAnnonces";
import { ANNONCE_CATEGORIES as categories, ANNONCE_URGENCY } from "@/lib/constants";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate, useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { DirectionalArrow } from "@/components/DirectionalArrow";
import { Link } from "react-router-dom";

const ModifierAnnonce = () => {
  const { locale, t } = useLanguage("annonces");
  const lNavigate = useLocalizedNavigate();
  const lp = useLocalizedPath();
  const allWilayasData = useWilayas(locale);
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { data: annonce, isLoading } = useAnnonceById(id);
  const updateAnnonce = useUpdateAnnonce();

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [urgency, setUrgency] = useState("");
  const [wilaya, setWilaya] = useState("");
  const [commune, setCommune] = useState("");
  const [addressHint, setAddressHint] = useState("");
  const [description, setDescription] = useState("");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");

  // Photos existantes (URLs déjà stockées)
  const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
  // Nouvelles photos à uploader
  const [newPhotoFiles, setNewPhotoFiles] = useState<File[]>([]);
  const [newPhotoPreviews, setNewPhotoPreviews] = useState<string[]>([]);

  const [initialized, setInitialized] = useState(false);

  // Communes pour la wilaya sélectionnée — city_name (FR) comme value pour la compatibilité DB
  const communeOptions = useCommunesByWilaya(wilaya, locale);

  // Redirection si pas propriétaire
  useEffect(() => {
    if (!isLoading && annonce && user && annonce.user_id !== user.id) {
      lNavigate("/mes-annonces");
    }
  }, [annonce, user, isLoading]);

  // Pré-remplissage du formulaire
  useEffect(() => {
    if (annonce && !initialized) {
      setTitle(annonce.title);
      setCategory(annonce.category || "");
      setUrgency(annonce.urgency || "");
      setWilaya(annonce.wilaya || "");
      setCommune(annonce.commune || "");
      setAddressHint(annonce.address_hint || "");
      setDescription(annonce.description || "");
      setBudgetMin(annonce.budget_min != null ? String(annonce.budget_min) : "");
      setBudgetMax(annonce.budget_max != null ? String(annonce.budget_max) : "");
      setExistingPhotos(annonce.photos || []);
      setInitialized(true);
    }
  }, [annonce, initialized]);

  const handleNewPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setNewPhotoFiles((prev) => [...prev, ...files]);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => setNewPhotoPreviews((prev) => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
    });
  };

  const removeExistingPhoto = (index: number) => {
    setExistingPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNewPhoto = (index: number) => {
    setNewPhotoFiles((prev) => prev.filter((_, i) => i !== index));
    setNewPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadNewPhotos = async (userId: string): Promise<string[]> => {
    const photoUrls: string[] = [];
    for (const file of newPhotoFiles) {
      const ext = file.name.split(".").pop();
      const path = `annonces/${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from("annonces").upload(path, file);
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from("annonces").getPublicUrl(path);
      photoUrls.push(urlData.publicUrl);
    }
    return photoUrls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !category || !urgency || !wilaya || !commune) {
      toast({ title: t("form.required_fields"), description: t("form.required_fields_desc"), variant: "destructive" });
      return;
    }
    if (!id || !user) return;

    try {
      const uploadedUrls = newPhotoFiles.length > 0 ? await uploadNewPhotos(user.id) : [];
      const allPhotos = [...existingPhotos, ...uploadedUrls];

      await updateAnnonce.mutateAsync({
        id,
        title,
        category,
        urgency,
        wilaya,
        commune,
        address_hint: addressHint || null,
        description: description || null,
        budget_min: budgetMin ? parseInt(budgetMin) : null,
        budget_max: budgetMax ? parseInt(budgetMax) : null,
        photos: allPhotos,
      });

      toast({ title: t("modifier.saved"), description: t("modifier.saved_desc") });
      lNavigate("/mes-annonces");
    } catch (error: any) {
      toast({ title: t("modifier.error"), description: error.message, variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container max-w-2xl py-8">
          <p className="text-center text-muted-foreground py-12">{t("common.loading")}</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (!annonce) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container max-w-2xl py-8">
          <p className="text-center text-muted-foreground py-12">{t("modifier.not_found")}</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container max-w-2xl py-8">
        <div className="mb-8">
          <Link to={lp("/mes-annonces")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
            <DirectionalArrow className="h-4 w-4 rotate-180" /> {t("modifier.back_link")}
          </Link>
          <h1 className="font-display text-3xl font-bold">{t("modifier.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("modifier.subtitle")}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 rounded-xl border bg-card p-6">

          {/* Titre */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_title")} *</Label>
            <Input placeholder={t("form.title_placeholder")} value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          {/* Catégorie */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_category")} *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder={t("form.category_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {t(`categories_all.${c.value}`, { defaultValue: c.label })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Urgence */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_urgency")} *</Label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger>
                <SelectValue placeholder={t("form.urgency_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                {ANNONCE_URGENCY.map((u) => (
                  <SelectItem key={u.value} value={u.value}>
                    {t(`urgency.${u.value}`, { defaultValue: u.label })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Localisation */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="font-display font-semibold">{t("form.label_wilaya")} *</Label>
              <Select value={wilaya} onValueChange={(v) => { setWilaya(v); setCommune(""); }}>
                <SelectTrigger>
                  <SelectValue placeholder={t("form.label_wilaya")} />
                </SelectTrigger>
                <SelectContent>
                  {allWilayasData
                    .sort((a, b) => a.label.localeCompare(b.label, locale === "ar" ? "ar" : "fr"))
                    .map((w) => (
                      <SelectItem key={w.state_name} value={w.state_name}>{w.label}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-display font-semibold">{t("form.label_commune")} *</Label>
              <Select value={commune} onValueChange={setCommune}>
                <SelectTrigger><SelectValue placeholder={t("form.label_commune")} /></SelectTrigger>
                <SelectContent>
                  {communeOptions.map((c) => (
                    <SelectItem key={c.city_name} value={c.city_name}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Indice d'adresse */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_address_hint")}</Label>
            <Input placeholder={t("form.address_hint_placeholder")} value={addressHint} onChange={(e) => setAddressHint(e.target.value)} />
            <p className="text-xs text-muted-foreground">{t("form.address_hint_help")}</p>
          </div>

          {/* Budget */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_budget")}</Label>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input type="number" placeholder="Min" value={budgetMin} onChange={(e) => setBudgetMin(e.target.value)} />
              <Input type="number" placeholder="Max" value={budgetMax} onChange={(e) => setBudgetMax(e.target.value)} />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="font-display font-semibold">{t("form.label_description")}</Label>
            <Textarea
              placeholder={t("form.description_placeholder")}
              className="min-h-[120px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Photos */}
          <div className="space-y-3">
            <Label className="font-display font-semibold">
              {t("form.label_photos")} <span className="text-muted-foreground font-normal">({t("common.optional")})</span>
            </Label>

            {/* Photos existantes */}
            {existingPhotos.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">{t("modifier.existing_photos")}</p>
                <div className="grid grid-cols-4 gap-2">
                  {existingPhotos.map((url, i) => (
                    <div key={i} className="relative group">
                      <img src={url} alt={`Photo ${i + 1}`} className="aspect-square rounded-lg object-cover border" />
                      <button
                        type="button"
                        onClick={() => removeExistingPhoto(i)}
                        className="absolute top-1 end-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nouvelles photos */}
            {newPhotoPreviews.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">{t("modifier.new_photos")}</p>
                <div className="grid grid-cols-4 gap-2">
                  {newPhotoPreviews.map((src, i) => (
                    <div key={i} className="relative group">
                      <img src={src} alt={`Nouvelle ${i}`} className="aspect-square rounded-lg object-cover border border-primary/30" />
                      <button
                        type="button"
                        onClick={() => removeNewPhoto(i)}
                        className="absolute top-1 end-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Zone d'ajout */}
            <label
              htmlFor="photo-upload-edit"
              className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 p-6 text-center transition-colors hover:border-primary/50 hover:bg-primary/10"
            >
              <Camera className="h-7 w-7 text-primary" />
              <p className="text-sm text-muted-foreground">{t("form.photo_click")}</p>
            </label>
            <input id="photo-upload-edit" type="file" accept="image/*" multiple className="hidden" onChange={handleNewPhotoChange} />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button type="button" variant="outline" className="flex-1" onClick={() => lNavigate("/mes-annonces")}>
              {t("common.cancel")}
            </Button>
            <Button variant="hero" size="xl" className="flex-1" type="submit" disabled={updateAnnonce.isPending}>
              <Save className="me-2 h-5 w-5" />
              {updateAnnonce.isPending ? t("form.saving") : t("form.save")}
            </Button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
};

export default ModifierAnnonce;
